import { z } from 'zod';
import { runSearch } from '../components/workflows/runs/runSearch.js';
import { SIGNAL_GROUPINGS, SIGNAL_TABS, type Signal } from './signals.js';

// Signals live in a section of HOME, so their URL state shares the dashboard's search. The keys are
// namespaced so they never collide with the dashboard's own filters.
export const SIGNALS_SECTION_ID = 'signals';

const signalsSearchSchema = z
  .object({
    signalsTab: z.enum(SIGNAL_TABS).optional().catch(undefined),
    signalsGroup: z.enum(SIGNAL_GROUPINGS).optional().catch(undefined),
    signalRun: z.uuid().optional().catch(undefined),
  })
  .catch({});

export type SignalsSearch = z.infer<typeof signalsSearchSchema>;

export function validateSignalsSearch(search: Record<string, unknown>): SignalsSearch {
  const parsed = signalsSearchSchema.parse(search);
  const result: SignalsSearch = {};
  if (parsed.signalsTab !== undefined) result.signalsTab = parsed.signalsTab;
  if (parsed.signalsGroup !== undefined) result.signalsGroup = parsed.signalsGroup;
  if (parsed.signalRun !== undefined) result.signalRun = parsed.signalRun;
  return result;
}

/** The old standalone /signals links (run, tab, group) as HOME's signals search. */
export function legacySignalsSearch(search: Record<string, unknown>): SignalsSearch {
  return validateSignalsSearch({
    signalsTab: search['tab'],
    signalsGroup: search['group'],
    signalRun: search['run'],
  });
}

/**
 * Where a Signal opens: its workflow on the run, scoped to the run's own accounts, baseline dates
 * and KPI (never the current page's filters). A Signal without a baseline uses the page's default
 * range; one without a KPI uses the page's default KPI.
 */
export function signalRunSearch(signal: Signal): Record<string, unknown> {
  const { range, leadingKpi: _defaultKpi, ...search } = runSearch({}, signal);
  const hasBaseline = signal.startDate !== '' && signal.endDate !== '';
  return {
    ...search,
    ...(hasBaseline ? { range } : {}),
    ...(signal.kpi === null ? {} : { leadingKpi: signal.kpi }),
  };
}
